import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { currentLang, t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useSave } from './manage/common.tsx';

/**
 * Calendar: the signed-in person's events and invitations, with colleagues' calendars beside them
 * when asked for, and what other modules put on their days (appointments, time off, task
 * deadlines, field visits). Day and week grids, a month and an agenda; events are created by
 * clicking or dragging on the grid, moved and stretched with the mouse, and planned with everyone's
 * free time in view. All times are the business's time zone, whatever the computer is set to.
 */

type ToneName = 'blue' | 'green' | 'orange' | 'purple' | 'red' | 'teal' | 'yellow' | 'grey';
type State = 'needs_action' | 'accepted' | 'tentative' | 'declined';
type View = 'day' | 'week' | 'month' | 'agenda';
type Scope = 'one' | 'following' | 'all';
type Repeat = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

interface Person {
  id: string;
  name: string;
  username: string;
}
interface Home {
  me: string;
  today: string;
  now: string;
  tz: string;
  people: Person[];
  sources: string[];
  reminders: number[];
}
interface Occ {
  id: string;
  occ: string | null;
  key: string;
  name: string;
  starts: string;
  ends: string;
  all_day: boolean;
  tone: ToneName;
  location: string | null;
  video: string | null;
  organizer: string;
  privacy: 'public' | 'private';
  show_as: 'busy' | 'free';
  recurring: boolean;
  reminder: number | null;
  state: State | null;
  people: { user: string; state: State }[];
  masked: boolean;
}
interface Item {
  source: string;
  key: string;
  user: string | null;
  title: string;
  starts?: string;
  ends?: string;
  day?: string;
  end_day?: string;
  link: string;
  busy: 'busy' | 'away' | 'none';
}
interface Range {
  from: string;
  to: string;
  now: string;
  events: Occ[];
  items: Item[];
}
interface Rule {
  repeat: Repeat;
  interval: number;
  weekdays: number[] | null;
  monthly_by: 'date' | 'weekday';
  until: string | null;
  count: number | null;
  start_day: string;
  rrule: string | null;
}
interface Detail {
  id: string;
  occ: string | null;
  name: string;
  starts: string;
  ends: string;
  all_day: boolean;
  location: string | null;
  video: string | null;
  description: string | null;
  organizer: { id: string; name: string };
  privacy: 'public' | 'private';
  show_as: 'busy' | 'free';
  tone: ToneName;
  reminder: number | null;
  rule: Rule;
  series: { id: string; name: string } | null;
  tz: string;
  people: { id: string; name: string; username: string; state: State; organizer: boolean }[];
  guests: { name: string; email: string; state: State }[];
  state: State | null;
  can: { edit: boolean; respond: boolean };
}
interface Invitation {
  id: string;
  occ: string | null;
  name: string;
  starts: string;
  all_day: boolean;
  recurring: boolean;
  organizer: string;
  tone: ToneName;
}

const TONES: ToneName[] = ['blue', 'green', 'orange', 'purple', 'red', 'teal', 'yellow', 'grey'];
/** Colleagues' calendars, in the order they were added. */
const PERSON_TONES: ToneName[] = ['teal', 'orange', 'purple', 'green', 'red', 'yellow', 'blue', 'grey'];
const SOURCE_STYLE: Record<string, { tone: ToneName; icon: IconName }> = {
  appointments: { tone: 'purple', icon: 'calendar' },
  time_off: { tone: 'grey', icon: 'sun' },
  holidays: { tone: 'yellow', icon: 'star' },
  tasks: { tone: 'orange', icon: 'checkCircle' },
  field: { tone: 'green', icon: 'wrench' },
};
const STATE_ICON: Record<State, IconName> = { accepted: 'check', tentative: 'help', declined: 'x', needs_action: 'clock' };
const STATE_TONE: Record<State, Tone> = { accepted: 'ok', tentative: 'warn', declined: 'danger', needs_action: 'neutral' };
const PX = 0.8; // pixels per minute: an hour is 48px
const SNAP = 15;
const DAY_MIN = 24 * 60;
const PREFS = 'erp.calendar.prefs';

// ---- dates and times where the business is ---------------------------------------------------

const ms = (d: string) => Date.parse(`${d}T00:00:00Z`);
const plusDays = (d: string, n: number) => new Date(ms(d) + n * 86_400_000).toISOString().slice(0, 10);
const dayDiff = (a: string, b: string) => Math.round((ms(b) - ms(a)) / 86_400_000);
const isoWeekday = (d: string) => {
  const w = new Date(ms(d)).getUTCDay();
  return w === 0 ? 7 : w;
};
const mondayOf = (d: string) => plusDays(d, 1 - isoWeekday(d));
const monthStart = (d: string) => `${d.slice(0, 8)}01`;
const plusMonths = (d: string, n: number) => {
  const [y, m] = d.split('-').map(Number) as [number, number];
  const at = new Date(Date.UTC(y, m - 1 + n, 1));
  return at.toISOString().slice(0, 10);
};
const formats = new Map<string, Intl.DateTimeFormat>();
const fmt = (key: string, make: () => Intl.DateTimeFormat) => {
  let f = formats.get(key);
  if (!f) formats.set(key, (f = make()));
  return f;
};
/** The local day and minute of the day of an instant. */
function where(at: string | number, tz: string) {
  const parts = fmt(
    `p:${tz}`,
    () => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
  ).formatToParts(new Date(at));
  const get = (x: string) => parts.find((p) => p.type === x)!.value;
  return { day: `${get('year')}-${get('month')}-${get('day')}`, min: Number(get('hour')) * 60 + Number(get('minute')) };
}
/** The instant of a local wall-clock time. */
function localTime(day: string, minute: number, tz: string): Date {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  const guess = Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)), h, m);
  const w = where(guess, tz);
  const shown = Date.UTC(Number(w.day.slice(0, 4)), Number(w.day.slice(5, 7)) - 1, Number(w.day.slice(8, 10)), Math.floor(w.min / 60), w.min % 60);
  return new Date(guess - (shown - guess));
}
const clockOf = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const timeFmt = (tz: string) => fmt(`t:${tz}:${currentLang()}`, () => new Intl.DateTimeFormat(currentLang(), { timeZone: tz, hour: 'numeric', minute: '2-digit' }));
const timeText = (at: string, tz: string) => timeFmt(tz).format(new Date(at));
/** "9:00 – 10:30 AM": as short as the language allows. */
const rangeText = (a: string, b: string, tz: string) => timeFmt(tz).formatRange(new Date(a), new Date(b));
const hourText = (at: Date, tz: string) => fmt(`h:${tz}:${currentLang()}`, () => new Intl.DateTimeFormat(currentLang(), { timeZone: tz, hour: 'numeric' })).format(at);
const dayText = (d: string, opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(currentLang(), { ...opts, timeZone: 'UTC' }).format(new Date(ms(d)));
const longDay = (d: string) => dayText(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const wdName = (w: number, style: 'short' | 'long' | 'narrow' = 'short') => dayText(plusDays('2024-01-01', w - 1), { weekday: style });
const ordinal = (n: number) => t(`cal.nth_${n === 5 ? 'last' : n}` as MessageKey);
const nthOfMonth = (d: string) => Math.floor((Number(d.slice(8, 10)) - 1) / 7) + 1;
const minutesText = (n: number) => (n === 0 ? t('cal.at_start') : n < 60 ? t('cal.min_before', { n }) : n < 1440 ? tn('cal.hours_before', n / 60) : tn('cal.days_before', n / 1440));

function whenText(e: { starts: string; ends: string; all_day: boolean }, tz: string) {
  const a = where(e.starts, tz);
  const b = where(new Date(Date.parse(e.ends) - (e.all_day ? 1 : 0)).toISOString(), tz);
  if (e.all_day) return a.day === b.day ? `${longDay(a.day)} · ${t('cal.all_day')}` : `${longDay(a.day)} – ${longDay(b.day)}`;
  if (a.day === b.day || (b.min === 0 && dayDiff(a.day, b.day) === 1)) return `${longDay(a.day)} · ${rangeText(e.starts, e.ends, tz)}`;
  return `${longDay(a.day)} ${timeText(e.starts, tz)} – ${longDay(b.day)} ${timeText(e.ends, tz)}`;
}

/** "Every week on Monday and Wednesday, until 31 December" and the like. */
function ruleText(r: Pick<Rule, 'repeat' | 'interval' | 'weekdays' | 'monthly_by' | 'until' | 'count'>, start: string) {
  if (r.repeat === 'none') return t('cal.no_repeat');
  const n = r.interval;
  let s = '';
  if (r.repeat === 'daily') s = n > 1 ? t('cal.every_n_days', { n }) : t('cal.every_day');
  if (r.repeat === 'weekly') {
    const days = (r.weekdays?.length ? r.weekdays : [isoWeekday(start)]).map((w) => wdName(w, 'long'));
    const list = new Intl.ListFormat(currentLang(), { type: 'conjunction' }).format(days);
    s = n > 1 ? t('cal.every_n_weeks_on', { n, days: list }) : t('cal.every_week_on', { days: list });
  }
  if (r.repeat === 'monthly') {
    const on = r.monthly_by === 'date' ? t('cal.on_day', { n: Number(start.slice(8, 10)) }) : t('cal.on_the', { nth: ordinal(nthOfMonth(start)), day: wdName(isoWeekday(start), 'long') });
    s = n > 1 ? t('cal.every_n_months', { n, on }) : t('cal.every_month', { on });
  }
  if (r.repeat === 'yearly')
    s = n > 1 ? t('cal.every_n_years', { n, on: dayText(start, { day: 'numeric', month: 'long' }) }) : t('cal.every_year', { on: dayText(start, { day: 'numeric', month: 'long' }) });
  if (r.count) s += `, ${tn('cal.times', r.count)}`;
  else if (r.until) s += `, ${t('cal.until', { day: dayText(r.until, { day: 'numeric', month: 'long', year: 'numeric' }) })}`;
  return s;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

function readPrefs(): { people: string[]; sources: string[] | null; view: View | null } {
  try {
    const p = JSON.parse(localStorage.getItem(PREFS) ?? '{}');
    return { people: Array.isArray(p.people) ? p.people : [], sources: Array.isArray(p.sources) ? p.sources : null, view: p.view ?? null };
  } catch {
    return { people: [], sources: null, view: null };
  }
}
const savePrefs = (p: { people: string[]; sources: string[]; view: View }) => {
  try {
    localStorage.setItem(PREFS, JSON.stringify(p));
  } catch {
    // private mode: the choice lasts for this visit
  }
};

const usePhone = () => {
  const q = '(max-width: 760px)';
  const [phone, setPhone] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setPhone(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return phone;
};

/** The days a view shows for an anchor day. */
function span(view: View, d: string): { from: string; to: string; days: string[] } {
  if (view === 'day') return { from: d, to: d, days: [d] };
  if (view === 'week') {
    const m = mondayOf(d);
    return { from: m, to: plusDays(m, 6), days: Array.from({ length: 7 }, (_, i) => plusDays(m, i)) };
  }
  if (view === 'month') {
    const first = mondayOf(monthStart(d));
    return { from: first, to: plusDays(first, 41), days: Array.from({ length: 42 }, (_, i) => plusDays(first, i)) };
  }
  return { from: d, to: plusDays(d, 29), days: Array.from({ length: 30 }, (_, i) => plusDays(d, i)) };
}

// ---- the screen ------------------------------------------------------------------------------

export function Calendar() {
  const [params, setParams] = useSearchParams();
  const phone = usePhone();
  const { fail } = useUi();
  const [home, setHome] = useState<Home | null>(null);
  const prefs = useMemo(readPrefs, []);
  const [people, setPeople] = useState<string[]>(prefs.people);
  const [sources, setSources] = useState<string[] | null>(prefs.sources);
  const [data, setData] = useState<Range | null>(null);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [editing, setEditing] = useState<EditorStart | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [pulse, setPulse] = useState(0);
  const wanted = (params.get('v') as View | null) ?? prefs.view ?? (phone ? 'agenda' : 'week');
  const view: View = phone && wanted === 'week' ? 'day' : wanted;
  const anchor = params.get('d') ?? home?.today ?? null;
  const open = params.get('e') ? { id: params.get('e')!, occ: params.get('o') } : null;
  const set = useCallback(
    (patch: Record<string, string | null>) => {
      const p = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null) p.delete(k);
        else p.set(k, v);
      }
      setParams(p, { replace: !('v' in patch || 'd' in patch) });
    },
    [params, setParams],
  );

  useEffect(() => {
    action<Home>('calendar.home').then((h) => {
      setHome(h);
      // Colleagues who left are dropped; sources follow what this person may see.
      setPeople((p) => p.filter((id) => h.people.some((x) => x.id === id) && id !== h.me));
      setSources((s) => (s ?? h.sources).filter((x) => h.sources.includes(x)));
    }, fail);
  }, [fail]);
  useEffect(() => {
    if (home && sources) savePrefs({ people, sources, view: wanted });
  }, [home, people, sources, wanted]);

  const range = anchor ? span(view, anchor) : null;
  const loadKey = range && sources ? JSON.stringify([range.from, range.to, people, sources]) : null;
  const load = useCallback(() => {
    if (!loadKey) return;
    const [from, to, users, src] = JSON.parse(loadKey) as [string, string, string[], string[]];
    action<Range>('calendar.range', { from, to, users, sources: src }).then((r) => setData(r), fail);
    action<{ items: Invitation[] }>('calendar.invitations').then(
      (r) => setInvites(r.items),
      () => undefined,
    );
  }, [loadKey, fail]);
  useEffect(load, [load]);
  // One live stream for the page; a slow check in case it misses something.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const stop = subscribe(['calendar'], (event, payload: { users?: string[] }) => {
      if (event === 'ready') return;
      if (payload.users && home && !payload.users.some((u) => u === home.me || people.includes(u))) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setPulse((n) => n + 1), 150);
    });
    const id = setInterval(() => setPulse((n) => n + 1), 120_000);
    return () => {
      stop();
      clearInterval(id);
      if (timer) clearTimeout(timer);
    };
  }, [home, people]);
  useEffect(() => {
    if (pulse) load();
  }, [pulse, load]);

  if (!home || !anchor || !range || !sources) {
    return (
      <Shell>
        <div className="page cal-page">
          <Skeleton h={560} r="var(--radius-lg)" />
        </div>
      </Shell>
    );
  }

  const go = (n: number) => {
    const d = view === 'month' ? plusMonths(anchor, n) : view === 'week' ? plusDays(anchor, 7 * n) : view === 'agenda' ? plusDays(anchor, 30 * n) : plusDays(anchor, n);
    set({ d });
  };
  const title =
    view === 'day'
      ? phone
        ? dayText(anchor, { weekday: 'short', day: 'numeric', month: 'short' })
        : longDay(anchor)
      : view === 'month'
        ? dayText(anchor, { month: 'long', year: 'numeric' })
        : view === 'week'
          ? weekTitle(range.from, range.to)
          : t('cal.agenda_from', { day: dayText(anchor, { day: 'numeric', month: phone ? 'short' : 'long' }) });
  const tone = (u: string) => PERSON_TONES[people.indexOf(u) % PERSON_TONES.length]!;
  const ctx: GridCtx = {
    home,
    data,
    people,
    tone,
    onOpen: (e) => set({ e: e.id, o: e.occ }),
    onItem: (i) => i.link,
    onCreate: (s) => setEditing(s),
    onMoved: load,
    onDay: (d) => set({ v: 'day', d }),
  };
  const views: View[] = phone ? ['day', 'month', 'agenda'] : ['day', 'week', 'month', 'agenda'];
  const side = (
    <Side
      home={home}
      anchor={anchor}
      range={range}
      people={people}
      setPeople={setPeople}
      sources={sources}
      setSources={setSources}
      tone={tone}
      invites={invites}
      onPick={(d) => {
        set({ d });
        setSideOpen(false);
      }}
      onNew={() => setEditing({ day: anchor === home.today ? home.today : anchor })}
      onOpen={(i) => set({ e: i.id, o: i.occ })}
      onAnswered={load}
    />
  );
  return (
    <Shell>
      <div className="page cal-page">
        <div className="cal">
          {phone ? null : side}
          <section className="panel cal-main" aria-label={t('cal.title')}>
            <header className="cal-bar">
              <div className="cal-nav">
                <Button size="sm" onClick={() => set({ d: home.today })} disabled={range.from <= home.today && range.to >= home.today && view !== 'agenda'}>
                  {t('cal.today')}
                </Button>
                <IconButton icon="back" label={t('cal.previous')} onClick={() => go(-1)} />
                <IconButton icon="chevronRight" label={t('cal.next')} onClick={() => go(1)} />
                <h1 className="cal-title" aria-live="polite">
                  {title}
                </h1>
              </div>
              <div className="cal-tools">
                {phone ? <IconButton icon="sliders" label={t('cal.calendars')} onClick={() => setSideOpen(true)} className={invites.length ? 'cal-dot' : ''} /> : null}
                <div className="segmented" role="tablist" aria-label={t('cal.views')}>
                  {views.map((v) => (
                    <button key={v} type="button" role="tab" aria-selected={view === v} className={view === v ? 'active' : ''} onClick={() => set({ v })}>
                      {t(`cal.v_${v}` as MessageKey)}
                    </button>
                  ))}
                </div>
                {phone ? <IconButton icon="plus" label={t('cal.new')} variant="primary" onClick={() => setEditing({ day: anchor })} /> : null}
              </div>
            </header>
            {view === 'day' || view === 'week' ? (
              <TimeGrid ctx={ctx} days={range.days} />
            ) : view === 'month' ? (
              <MonthGrid ctx={ctx} days={range.days} month={anchor.slice(0, 7)} phone={phone} />
            ) : (
              <Agenda ctx={ctx} days={range.days} />
            )}
          </section>
        </div>
      </div>
      {phone && sideOpen ? (
        <div className="overlay cal-drawer-back" onMouseDown={(e) => e.target === e.currentTarget && setSideOpen(false)}>
          <div className="cal-drawer" role="dialog" aria-modal="true" aria-label={t('cal.calendars')}>
            <header className="dialog-head">
              <h2>{t('cal.calendars')}</h2>
              <IconButton icon="x" label={t('common.close')} onClick={() => setSideOpen(false)} />
            </header>
            {side}
          </div>
        </div>
      ) : null}
      {open ? (
        <EventDialog
          key={`${open.id}|${open.occ}`}
          id={open.id}
          occ={open.occ}
          home={home}
          onClose={() => set({ e: null, o: null })}
          onChanged={load}
          onEdit={(d, mode) => {
            set({ e: null, o: null });
            setEditing({ detail: d, duplicate: mode === 'duplicate' });
          }}
        />
      ) : null}
      {editing ? (
        <Editor
          start={editing}
          home={home}
          onClose={() => setEditing(null)}
          onSaved={(id) => {
            setEditing(null);
            load();
            if (id) set({ e: id, o: null });
          }}
        />
      ) : null}
    </Shell>
  );
}

function weekTitle(from: string, to: string) {
  const f = new Intl.DateTimeFormat(currentLang(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  return f.formatRange(new Date(ms(from)), new Date(ms(to)));
}

interface GridCtx {
  home: Home;
  data: Range | null;
  people: string[];
  tone: (u: string) => ToneName;
  onOpen: (e: Occ) => void;
  onItem: (i: Item) => string;
  onCreate: (s: EditorStart) => void;
  onMoved: () => void;
  onDay: (d: string) => void;
}

/** How an event looks: its own colour when it is on my calendar, the colleague's colour when only on theirs. */
function look(ctx: GridCtx, e: Occ): { tone: ToneName; mine: boolean; cls: string } {
  const mine = e.state !== null || e.organizer === ctx.home.me;
  const owner = e.people.find((p) => p.user !== ctx.home.me && ctx.people.includes(p.user));
  const tone = mine ? e.tone : owner ? ctx.tone(owner.user) : e.tone;
  const cls = [
    'cal-ev',
    `tone-${tone}`,
    mine ? '' : 'other',
    e.state === 'needs_action' ? 'unanswered' : '',
    e.state === 'tentative' ? 'maybe' : '',
    e.state === 'declined' ? 'declined' : '',
    e.show_as === 'free' ? 'free' : '',
    e.masked ? 'masked' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return { tone, mine, cls };
}
const stateLabel = (s: State | null) => (s && s !== 'accepted' ? t(`cal.st_${s}` as MessageKey) : '');

function evLabel(e: Occ, tz: string) {
  if (e.masked) return t('cal.busy');
  const when = e.all_day ? t('cal.all_day') : rangeText(e.starts, e.ends, tz);
  return [e.name, when, e.location, stateLabel(e.state)].filter(Boolean).join(', ');
}

// ---- day and week ------------------------------------------------------------------------------

interface Seg {
  e?: Occ;
  i?: Item;
  key: string;
  day: string;
  from: number;
  to: number;
  col: number;
  cols: number;
  /** Starts before this day or goes on after it. */
  cutTop: boolean;
  cutBottom: boolean;
}

/** Timed things split into days and laid out side by side where they overlap. */
function layout(days: string[], things: { key: string; starts: string; ends: string; e?: Occ; i?: Item }[], tz: string) {
  const byDay = new Map<string, Seg[]>(days.map((d) => [d, []]));
  for (const x of things) {
    const a = where(x.starts, tz);
    const b = where(x.ends, tz);
    for (const d of days) {
      if (d < a.day || d > b.day || (d === b.day && b.min === 0 && d !== a.day)) continue;
      const from = d === a.day ? a.min : 0;
      const to = d === b.day ? b.min : DAY_MIN;
      byDay.get(d)!.push({
        e: x.e,
        i: x.i,
        key: `${x.key}@${d}`,
        day: d,
        from,
        to: Math.max(to, from + 15),
        col: 0,
        cols: 1,
        cutTop: d !== a.day,
        cutBottom: d !== b.day && !(b.min === 0 && plusDays(d, 1) === b.day),
      });
    }
  }
  for (const segs of byDay.values()) {
    segs.sort((p, q) => p.from - q.from || q.to - p.to);
    let cluster: Seg[] = [];
    let end = -1;
    const close = () => {
      const cols = Math.max(1, ...cluster.map((s) => s.col + 1));
      for (const s of cluster) s.cols = cols;
      cluster = [];
    };
    for (const s of segs) {
      if (s.from >= end && cluster.length) close();
      const used = new Set(cluster.filter((c) => c.to > s.from).map((c) => c.col));
      let col = 0;
      while (used.has(col)) col++;
      s.col = col;
      cluster.push(s);
      end = Math.max(end, s.to);
    }
    if (cluster.length) close();
  }
  return byDay;
}

interface Drag {
  kind: 'move' | 'resize' | 'create';
  seg?: Seg;
  startX: number;
  startY: number;
  day: string;
  from: number;
  to: number;
  moved: boolean;
  /** Opens on release, never moves. */
  fixed?: boolean;
}

function TimeGrid({ ctx, days }: { ctx: GridCtx; days: string[] }) {
  const { home, data } = ctx;
  const tz = home.tz;
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const scroller = useRef<HTMLDivElement>(null);
  const cols = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const nowAt = where(now, tz);
  // Open around now on today, else at the start of the working day.
  useEffect(() => {
    const target = days.includes(nowAt.day) ? nowAt.min - 120 : 7 * 60;
    scroller.current?.scrollTo({ top: Math.max(0, target * PX) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days[0], days.length]);

  const events = data?.events ?? [];
  const items = data?.items ?? [];
  const allDay = (d: string): { e?: Occ; i?: Item }[] => [
    ...events.filter((e) => (e.all_day || Date.parse(e.ends) - Date.parse(e.starts) >= 86_400_000) && coversDay(e, d, tz)).map((e) => ({ e })),
    ...items.filter((i) => i.day && i.day <= d && (i.end_day ?? i.day) >= d).map((i) => ({ i })),
  ];
  const timed = [
    ...events.filter((e) => !e.all_day && Date.parse(e.ends) - Date.parse(e.starts) < 86_400_000).map((e) => ({ key: e.key, starts: e.starts, ends: e.ends, e })),
    ...items.filter((i) => i.starts && i.ends).map((i) => ({ key: i.key, starts: i.starts!, ends: i.ends!, i })),
  ];
  const segs = layout(days, timed, tz);

  const minuteAt = (clientY: number) => {
    const r = cols.current!.getBoundingClientRect();
    return Math.max(0, Math.min(DAY_MIN, (clientY - r.top) / PX));
  };
  const dayAt = (clientX: number) => {
    const r = cols.current!.getBoundingClientRect();
    const i = Math.floor(((clientX - r.left) / r.width) * days.length);
    return days[Math.max(0, Math.min(days.length - 1, i))]!;
  };
  const snap = (m: number) => Math.round(m / SNAP) * SNAP;
  const canDrag = (e?: Occ) => !!e && !e.masked && e.organizer === home.me;

  const begin = (ev: React.PointerEvent, d: Drag) => {
    if (ev.button !== 0) return;
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    dragRef.current = d;
    setDrag(d);
  };
  const onMove = (ev: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.fixed) return;
    const dy = ev.clientY - d.startY;
    const dx = ev.clientX - d.startX;
    if (!d.moved && Math.abs(dy) < 5 && Math.abs(dx) < 5) return;
    let next: Drag;
    if (d.kind === 'create') {
      const m = snap(minuteAt(ev.clientY));
      const a = snap(minuteAt(d.startY));
      next = { ...d, moved: true, from: Math.min(a, m), to: Math.max(a, m, Math.min(a, m) + SNAP) };
    } else if (d.kind === 'resize') {
      const to = Math.max(d.seg!.from + SNAP, Math.min(DAY_MIN, snap(d.seg!.to + dy / PX)));
      next = { ...d, moved: true, to };
    } else {
      const shift = snap(dy / PX);
      const len = d.seg!.to - d.seg!.from;
      const from = Math.max(0, Math.min(DAY_MIN - len, d.seg!.from + shift));
      next = { ...d, moved: true, day: dayAt(ev.clientX), from, to: from + len };
    }
    dragRef.current = next;
    setDrag(next);
  };
  const onUp = async (ev: React.PointerEvent) => {
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d) return;
    if (d.kind === 'create') {
      const from = d.moved ? d.from : Math.floor(minuteAt(ev.clientY) / 30) * 30;
      const to = d.moved ? d.to : Math.min(DAY_MIN, from + 60);
      ctx.onCreate({ day: d.day, from, to });
      return;
    }
    const e = d.seg!.e;
    if (!d.moved) {
      if (e && !e.masked) ctx.onOpen(e);
      else if (d.seg!.i) navigate(ctx.onItem(d.seg!.i));
      return;
    }
    if (!e) return;
    // The new start and end, kept whole when the event went past this day.
    const startShift = d.kind === 'move' ? dayDiff(d.seg!.day, d.day) * DAY_MIN + (d.from - d.seg!.from) : 0;
    const endShift = d.kind === 'move' ? startShift : d.to - d.seg!.to;
    const starts = new Date(Date.parse(e.starts) + startShift * 60_000).toISOString();
    const ends = new Date(Date.parse(e.ends) + endShift * 60_000).toISOString();
    if (starts === e.starts && ends === e.ends) return;
    try {
      await action('calendar.move', { id: e.id, occ: e.occ, starts, ends });
      toast(e.occ ? t('cal.moved_one') : t('cal.moved'));
      ctx.onMoved();
    } catch (err) {
      fail(err);
    }
  };

  const hours = Array.from({ length: 24 }, (_, h) => h * 60);
  return (
    <div className={`cal-grid-wrap days-${days.length}`}>
      <div className="cal-head" style={{ '--days': days.length } as CSSProperties}>
        <div className="cal-gutter" />
        {days.map((d) => (
          <button key={d} type="button" className={`cal-dayhead${d === home.today ? ' today' : ''}`} onClick={() => ctx.onDay(d)} aria-label={longDay(d)}>
            <span className="cal-wd">{wdName(isoWeekday(d))}</span>
            <span className="cal-dn">{Number(d.slice(8, 10))}</span>
          </button>
        ))}
        <div className="cal-gutter cal-allday-label">{t('cal.all_day')}</div>
        {days.map((d) => {
          const list = allDay(d);
          return (
            <div key={d} className="cal-allday" onClick={(e) => e.target === e.currentTarget && ctx.onCreate({ day: d, allDay: true })} role="presentation" title={t('cal.click_new_allday')}>
              {list.slice(0, 3).map((x) => (x.e ? <EventChip key={x.e.key} ctx={ctx} e={x.e} compact /> : <ItemChip key={x.i!.key} ctx={ctx} i={x.i!} />))}
              {list.length > 3 ? (
                <button type="button" className="cal-more" onClick={() => ctx.onDay(d)}>
                  {t('cal.more', { n: list.length - 3 })}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="cal-scroll" ref={scroller}>
        <div className="cal-body" style={{ '--days': days.length, height: DAY_MIN * PX } as CSSProperties}>
          <div className="cal-hours">
            {hours.map((h) =>
              h ? (
                <span key={h} className="cal-hour" style={{ top: h * PX }}>
                  {hourText(localTime(days[0]!, h, tz), tz)}
                </span>
              ) : null,
            )}
          </div>
          <div className="cal-cols" ref={cols} onPointerMove={onMove} onPointerUp={(e) => void onUp(e)} onPointerCancel={() => ((dragRef.current = null), setDrag(null))}>
            {days.map((d) => (
              <div
                key={d}
                className={`cal-col${d === home.today ? ' today' : ''}${isoWeekday(d) > 5 ? ' weekend' : ''}`}
                onPointerDown={(ev) => {
                  if (ev.target !== ev.currentTarget) return;
                  if (ev.pointerType !== 'mouse') {
                    // On a touch screen a tap makes a new event; dragging scrolls.
                    const from = Math.floor(minuteAt(ev.clientY) / 30) * 30;
                    ctx.onCreate({ day: d, from, to: Math.min(DAY_MIN, from + 60) });
                    return;
                  }
                  begin(ev, { kind: 'create', startX: ev.clientX, startY: ev.clientY, day: d, from: 0, to: 0, moved: false });
                }}
              >
                {hours.map((h) => (
                  <span key={h} className="cal-line" style={{ top: h * PX }} />
                ))}
                {segs.get(d)!.map((s) => {
                  const dragging = drag?.seg?.key === s.key && drag.moved;
                  const style: CSSProperties = {
                    top: s.from * PX,
                    height: Math.max((s.to - s.from) * PX - 2, 18),
                    left: `calc(${(s.col / s.cols) * 100}% + 2px)`,
                    width: `calc(${(1 / s.cols) * 100}% - 4px)`,
                  };
                  const short = (s.to - s.from) * PX < 36;
                  if (s.i) {
                    const st = SOURCE_STYLE[s.i.source] ?? { tone: 'grey', icon: 'calendar' };
                    return (
                      <button
                        key={s.key}
                        type="button"
                        className={`cal-ev item tone-${st.tone}${short ? ' short' : ''}`}
                        style={style}
                        onClick={() => navigate(ctx.onItem(s.i!))}
                        aria-label={`${t(`cal.src_${s.i.source}` as MessageKey)}: ${s.i.title}, ${rangeText(s.i.starts!, s.i.ends!, tz)}`}
                      >
                        <span className="cal-ev-title">
                          <Icon name={st.icon} size={12} /> {s.i.title}
                        </span>
                        {short ? null : <span className="cal-ev-sub">{rangeText(s.i.starts!, s.i.ends!, tz)}</span>}
                      </button>
                    );
                  }
                  const e = s.e!;
                  const { cls } = look(ctx, e);
                  const movable = canDrag(e);
                  return (
                    <div
                      key={s.key}
                      role="button"
                      tabIndex={e.masked ? -1 : 0}
                      className={`${cls}${short ? ' short' : ''}${dragging ? ' ghost' : ''}${movable ? ' movable' : ''}${s.cutTop ? ' cut-top' : ''}${s.cutBottom ? ' cut-bottom' : ''}`}
                      style={style}
                      aria-label={evLabel(e, tz)}
                      aria-disabled={e.masked || undefined}
                      onKeyDown={(k) => (k.key === 'Enter' || k.key === ' ') && !e.masked && (k.preventDefault(), ctx.onOpen(e))}
                      onPointerDown={(ev) => {
                        ev.stopPropagation();
                        if (e.masked) return;
                        // Only the organiser moves an event, and only with a mouse: on a touch screen dragging scrolls.
                        begin(ev, {
                          kind: 'move',
                          seg: { ...s },
                          startX: ev.clientX,
                          startY: ev.clientY,
                          day: s.day,
                          from: s.from,
                          to: s.to,
                          moved: false,
                          fixed: !movable || ev.pointerType !== 'mouse',
                        });
                      }}
                    >
                      <EventFace e={e} tz={tz} short={short} />
                      {movable && !s.cutBottom ? (
                        <span
                          className="cal-resize"
                          aria-hidden="true"
                          onPointerDown={(ev) => {
                            ev.stopPropagation();
                            begin(ev, { kind: 'resize', seg: { ...s }, startX: ev.clientX, startY: ev.clientY, day: s.day, from: s.from, to: s.to, moved: false });
                          }}
                        />
                      ) : null}
                    </div>
                  );
                })}
                {drag && drag.moved && drag.day === d && (drag.kind !== 'resize' || drag.seg?.day === d) ? (
                  <span
                    className={`cal-drop${drag.kind === 'create' ? ' new' : ''}`}
                    style={{
                      top: (drag.kind === 'resize' ? drag.seg!.from : drag.from) * PX,
                      height: Math.max(((drag.kind === 'resize' ? drag.to : drag.to) - (drag.kind === 'resize' ? drag.seg!.from : drag.from)) * PX - 2, 16),
                    }}
                    aria-hidden="true"
                  >
                    {clockOf(drag.kind === 'resize' ? drag.seg!.from : drag.from)} – {clockOf(drag.to)}
                    {drag.kind === 'create' ? null : <strong>{drag.seg?.e?.name}</strong>}
                  </span>
                ) : null}
                {d === nowAt.day ? <span className="cal-now" style={{ top: nowAt.min * PX }} aria-hidden="true" /> : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const coversDay = (e: Occ, d: string, tz: string) => {
  const a = where(e.starts, tz).day;
  const b = where(Date.parse(e.ends) - 1, tz).day;
  return a <= d && b >= d;
};

function EventFace({ e, tz, short }: { e: Occ; tz: string; short?: boolean }) {
  if (e.masked) {
    return (
      <span className="cal-ev-title">
        <Icon name="lock" size={12} /> {t('cal.busy')}
      </span>
    );
  }
  return (
    <>
      <span className="cal-ev-title">
        {e.state === 'needs_action' ? <Icon name="clock" size={12} /> : e.state === 'tentative' ? <Icon name="help" size={12} /> : null}
        {e.privacy === 'private' ? <Icon name="lock" size={12} /> : null}
        <span className="cal-ev-name">{e.name}</span>
        {e.recurring ? <Icon name="repeat" size={12} /> : null}
      </span>
      {short ? null : (
        <span className="cal-ev-sub">
          {rangeText(e.starts, e.ends, tz)}
          {e.location ? ` · ${e.location}` : ''}
        </span>
      )}
    </>
  );
}

function EventChip({ ctx, e, compact }: { ctx: GridCtx; e: Occ; compact?: boolean }) {
  const { cls } = look(ctx, e);
  const tz = ctx.home.tz;
  const timedLabel = !e.all_day && !compact ? `${timeText(e.starts, tz)} ` : '';
  return (
    <button type="button" className={`${cls} chip-ev${e.all_day || compact ? ' solid' : ''}`} onClick={() => !e.masked && ctx.onOpen(e)} aria-label={evLabel(e, tz)} disabled={e.masked}>
      {e.masked ? <Icon name="lock" size={12} /> : e.all_day || compact ? null : <span className="cal-chip-dot" aria-hidden="true" />}
      {timedLabel ? <span className="cal-chip-time">{timedLabel}</span> : null}
      <span className="cal-ev-name">{e.masked ? t('cal.busy') : e.name}</span>
      {e.state === 'needs_action' ? <Icon name="clock" size={12} /> : null}
    </button>
  );
}

function ItemChip({ ctx, i }: { ctx: GridCtx; i: Item }) {
  const navigate = useNavigate();
  const st = SOURCE_STYLE[i.source] ?? { tone: 'grey' as ToneName, icon: 'calendar' as IconName };
  const who = i.user && i.user !== ctx.home.me ? ctx.home.people.find((p) => p.id === i.user)?.name : null;
  const label = i.title || t(`cal.src_${i.source}` as MessageKey);
  return (
    <button
      type="button"
      className={`cal-ev item chip-ev solid tone-${st.tone}`}
      onClick={() => navigate(ctx.onItem(i))}
      aria-label={`${t(`cal.src_${i.source}` as MessageKey)}: ${label}${who ? `, ${who}` : ''}`}
    >
      <Icon name={st.icon} size={12} />
      <span className="cal-ev-name">
        {who ? `${who}: ` : ''}
        {i.source === 'time_off' && !i.title ? t('cal.away') : label}
      </span>
    </button>
  );
}

// ---- month ---------------------------------------------------------------------------------------

function MonthGrid({ ctx, days, month, phone }: { ctx: GridCtx; days: string[]; month: string; phone: boolean }) {
  const { home, data } = ctx;
  const tz = home.tz;
  const events = data?.events ?? [];
  const items = data?.items ?? [];
  const MAX = 3;
  return (
    <div className="cal-month">
      <div className="cal-month-head">
        {[1, 2, 3, 4, 5, 6, 7].map((w) => (
          <span key={w}>{wdName(w)}</span>
        ))}
      </div>
      <div className="cal-month-grid">
        {days.map((d) => {
          const list = [
            ...events.filter((e) => coversDay(e, d, tz)).map((e) => ({ k: e.key, all: e.all_day, at: e.starts, tone: look(ctx, e).tone, node: <EventChip key={e.key} ctx={ctx} e={e} /> })),
            ...items
              .filter((i) => (i.day ? i.day <= d && (i.end_day ?? i.day) >= d : i.starts && where(i.starts, tz).day === d))
              .map((i) => ({ k: i.key, all: !!i.day, at: i.starts ?? '', tone: (SOURCE_STYLE[i.source]?.tone ?? 'grey') as string, node: <ItemChip key={i.key} ctx={ctx} i={i} /> })),
          ].sort((a, b) => Number(b.all) - Number(a.all) || a.at.localeCompare(b.at));
          const cls = `cal-mday${d.slice(0, 7) !== month ? ' out' : ''}${d === home.today ? ' today' : ''}${isoWeekday(d) > 5 ? ' weekend' : ''}`;
          // On a phone a day shows coloured dots and opens the day: chips would be too narrow to read.
          if (phone)
            return (
              <button key={d} type="button" className={`${cls} dots`} onClick={() => ctx.onDay(d)} aria-label={t('cal.day_events', { day: longDay(d), n: list.length })}>
                <span className="cal-mday-n">{Number(d.slice(8, 10))}</span>
                <span className="cal-dots" aria-hidden="true">
                  {list.slice(0, 4).map((x) => (
                    <i key={x.k} className={`tone-${x.tone}`} />
                  ))}
                  {list.length > 4 ? <small>+{list.length - 4}</small> : null}
                </span>
              </button>
            );
          return (
            <div key={d} className={cls} onClick={(e) => e.target === e.currentTarget && ctx.onCreate({ day: d, from: 9 * 60, to: 10 * 60 })} role="presentation">
              <button type="button" className="cal-mday-n" onClick={() => ctx.onDay(d)} aria-label={longDay(d)}>
                {Number(d.slice(8, 10))}
              </button>
              <div className="cal-mday-list">{list.slice(0, MAX).map((x) => x.node)}</div>
              {list.length > MAX ? (
                <button type="button" className="cal-more" onClick={() => ctx.onDay(d)}>
                  {t('cal.more', { n: list.length - MAX })}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- agenda --------------------------------------------------------------------------------------

function Agenda({ ctx, days }: { ctx: GridCtx; days: string[] }) {
  const { home, data } = ctx;
  const tz = home.tz;
  const navigate = useNavigate();
  if (!data) return <Skeleton h={400} />;
  const rows = days
    .map((d) => ({
      d,
      events: data.events.filter((e) => coversDay(e, d, tz)),
      items: data.items.filter((i) => (i.day ? i.day <= d && (i.end_day ?? i.day) >= d : i.starts && where(i.starts, tz).day === d)),
    }))
    .filter((r) => r.events.length || r.items.length || r.d === home.today);
  if (!rows.some((r) => r.events.length || r.items.length))
    return (
      <EmptyState
        icon="calendar"
        title={t('cal.agenda_empty')}
        body={t('cal.agenda_empty_hint')}
        action={
          <Button variant="primary" icon="plus" onClick={() => ctx.onCreate({ day: days[0]! })}>
            {t('cal.new')}
          </Button>
        }
      />
    );
  return (
    <ol className="cal-agenda">
      {rows.map((r) => (
        <li key={r.d} className={`cal-ag-day${r.d === home.today ? ' today' : ''}`}>
          <h2 className="cal-ag-date">
            <span className="cal-ag-dn">{Number(r.d.slice(8, 10))}</span>
            <span>
              <strong>{r.d === home.today ? t('cal.today') : dayText(r.d, { weekday: 'long' })}</strong>
              <span className="muted small">{dayText(r.d, { month: 'long', year: 'numeric' })}</span>
            </span>
          </h2>
          <ul className="cal-ag-list">
            {!r.events.length && !r.items.length ? <li className="muted small cal-ag-none">{t('cal.nothing_today')}</li> : null}
            {r.items
              .filter((i) => i.day)
              .map((i) => (
                <li key={i.key}>
                  <ItemChip ctx={ctx} i={i} />
                </li>
              ))}
            {([...r.events.map((e) => ({ at: e.all_day ? '' : e.starts, e })), ...r.items.filter((i) => !i.day).map((i) => ({ at: i.starts!, i }))] as { at: string; e?: Occ; i?: Item }[])
              .sort((x, y) => (x.at < y.at ? -1 : x.at > y.at ? 1 : 0))
              .map(({ e, i }) => {
                if (e) {
                  const { tone } = look(ctx, e);
                  return (
                    <li key={e.key}>
                      <button type="button" className={`cal-ag-row${e.state === 'declined' ? ' declined' : ''}`} onClick={() => !e.masked && ctx.onOpen(e)} disabled={e.masked}>
                        <span className="cal-ag-time">{e.all_day ? t('cal.all_day') : rangeText(e.starts, e.ends, tz)}</span>
                        <span className={`cal-ag-bar tone-${tone}`} aria-hidden="true" />
                        <span className="cal-ag-main">
                          <strong>
                            {e.masked ? t('cal.busy') : e.name} {e.recurring ? <Icon name="repeat" size={14} /> : null}
                          </strong>
                          {e.location || e.video ? (
                            <span className="muted small">
                              {e.location ? (
                                <>
                                  <Icon name="pin" size={12} /> {e.location}
                                </>
                              ) : null}{' '}
                              {e.video ? (
                                <>
                                  <Icon name="video" size={12} /> {t('cal.video_call')}
                                </>
                              ) : null}
                            </span>
                          ) : null}
                        </span>
                        {e.state && e.state !== 'accepted' ? <StatusBadge tone={STATE_TONE[e.state]} icon={STATE_ICON[e.state]} label={t(`cal.st_${e.state}` as MessageKey)} /> : null}
                      </button>
                    </li>
                  );
                }
                i = i!;
                const st = SOURCE_STYLE[i.source] ?? { tone: 'grey' as ToneName, icon: 'calendar' as IconName };
                return (
                  <li key={i.key}>
                    <button type="button" className="cal-ag-row item" onClick={() => navigate(i.link)}>
                      <span className="cal-ag-time">{rangeText(i.starts!, i.ends!, tz)}</span>
                      <span className={`cal-ag-bar tone-${st.tone}`} aria-hidden="true" />
                      <span className="cal-ag-main">
                        <strong>{i.title}</strong>
                        <span className="muted small">
                          <Icon name={st.icon} size={12} /> {t(`cal.src_${i.source}` as MessageKey)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </li>
      ))}
    </ol>
  );
}

// ---- the side: month, invitations, calendars -------------------------------------------------------

function Side({
  home,
  anchor,
  range,
  people,
  setPeople,
  sources,
  setSources,
  tone,
  invites,
  onPick,
  onNew,
  onOpen,
  onAnswered,
}: {
  home: Home;
  anchor: string;
  range: { from: string; to: string };
  people: string[];
  setPeople: (p: string[]) => void;
  sources: string[];
  setSources: (s: string[]) => void;
  tone: (u: string) => ToneName;
  invites: Invitation[];
  onPick: (d: string) => void;
  onNew: () => void;
  onOpen: (i: Invitation) => void;
  onAnswered: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const { fail, toast } = useUi();
  const answer = async (i: Invitation, state: State) => {
    try {
      await action('calendar.respond', { id: i.id, occ: i.occ, state, scope: 'all' });
      toast(t(`cal.answered_${state}` as MessageKey));
      onAnswered();
    } catch (err) {
      fail(err);
    }
  };
  const others = home.people.filter((p) => p.id !== home.me && !people.includes(p.id) && (!q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase())));
  return (
    <aside className="panel cal-side">
      <Button variant="primary" icon="plus" onClick={onNew} className="cal-new">
        {t('cal.new')}
      </Button>
      <MiniMonth anchor={anchor} today={home.today} range={range} onPick={onPick} />
      {invites.length ? (
        <section className="cal-sec" aria-labelledby="cal-inv">
          <h2 id="cal-inv" className="cal-sec-title">
            {t('cal.invitations')} <span className="cal-count">{invites.length}</span>
          </h2>
          <ul className="cal-invites">
            {invites.slice(0, 5).map((i) => (
              <li key={`${i.id}|${i.occ}`} className="cal-invite">
                <button type="button" className="cal-invite-main" onClick={() => onOpen(i)}>
                  <span className={`cal-swatch tone-${i.tone}`} aria-hidden="true" />
                  <span>
                    <strong>{i.name}</strong>
                    <span className="muted small">
                      {i.all_day
                        ? dayText(where(i.starts, home.tz).day, { weekday: 'short', day: 'numeric', month: 'short' })
                        : `${dayText(where(i.starts, home.tz).day, { weekday: 'short', day: 'numeric', month: 'short' })} ${timeText(i.starts, home.tz)}`}
                      {i.recurring ? ` · ${t('cal.repeats')}` : ''}
                    </span>
                    <span className="muted small">{t('cal.from_person', { name: i.organizer })}</span>
                  </span>
                </button>
                <div className="cal-invite-acts" role="group" aria-label={t('cal.answer_for', { name: i.name })}>
                  <IconButton icon="check" label={t('cal.yes')} onClick={() => void answer(i, 'accepted')} className="cal-yes" />
                  <IconButton icon="help" label={t('cal.maybe')} onClick={() => void answer(i, 'tentative')} />
                  <IconButton icon="x" label={t('cal.no')} onClick={() => void answer(i, 'declined')} />
                </div>
              </li>
            ))}
          </ul>
          {invites.length > 5 ? <p className="muted small">{t('cal.more_invites', { n: invites.length - 5 })}</p> : null}
        </section>
      ) : null}
      {home.sources.length ? (
        <section className="cal-sec" aria-labelledby="cal-src">
          <h2 id="cal-src" className="cal-sec-title">
            {t('cal.also_show')}
          </h2>
          <ul className="cal-checks">
            {home.sources.map((s) => {
              const st = SOURCE_STYLE[s] ?? { tone: 'grey' as ToneName, icon: 'calendar' as IconName };
              const on = sources.includes(s);
              return (
                <li key={s}>
                  <label className="cal-check">
                    <input type="checkbox" checked={on} onChange={() => setSources(on ? sources.filter((x) => x !== s) : [...sources, s])} />
                    <span className={`cal-swatch tone-${st.tone}`} aria-hidden="true">
                      <Icon name={st.icon} size={12} />
                    </span>
                    {t(`cal.src_${s}` as MessageKey)}
                  </label>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      <section className="cal-sec" aria-labelledby="cal-ppl">
        <h2 id="cal-ppl" className="cal-sec-title">
          {t('cal.colleagues')}
          <IconButton icon={adding ? 'x' : 'plus'} label={adding ? t('common.close') : t('cal.add_colleague')} onClick={() => setAdding(!adding)} />
        </h2>
        {people.length ? (
          <ul className="cal-checks">
            {people.map((id) => {
              const p = home.people.find((x) => x.id === id);
              return p ? (
                <li key={id} className="cal-person">
                  <span className={`cal-avatar tone-${tone(id)}`} aria-hidden="true">
                    {initials(p.name)}
                  </span>
                  <span className="cal-person-name">{p.name}</span>
                  <IconButton icon="x" label={t('cal.hide_colleague', { name: p.name })} onClick={() => setPeople(people.filter((x) => x !== id))} />
                </li>
              ) : null;
            })}
          </ul>
        ) : !adding ? (
          <p className="muted small">{t('cal.colleagues_hint')}</p>
        ) : null}
        {adding ? (
          <div className="cal-add">
            <div className="ds-search">
              <Icon name="search" size={16} />
              <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('cal.find_colleague')} aria-label={t('cal.find_colleague')} autoFocus />
            </div>
            <ul className="cal-pick">
              {others.slice(0, 8).map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    disabled={people.length >= 12}
                    onClick={() => {
                      setPeople([...people, p.id]);
                      setQ('');
                    }}
                  >
                    <Icon name="plus" size={14} /> {p.name}
                  </button>
                </li>
              ))}
              {!others.length ? <li className="muted small">{t('cal.nobody_found')}</li> : null}
            </ul>
          </div>
        ) : null}
      </section>
    </aside>
  );
}

function MiniMonth({ anchor, today, range, onPick }: { anchor: string; today: string; range: { from: string; to: string }; onPick: (d: string) => void }) {
  const [month, setMonth] = useState(monthStart(anchor));
  useEffect(() => setMonth(monthStart(anchor)), [anchor]);
  const first = mondayOf(month);
  const days = Array.from({ length: 42 }, (_, i) => plusDays(first, i));
  return (
    <div className="cal-mini">
      <div className="cal-mini-head">
        <strong>{dayText(month, { month: 'long', year: 'numeric' })}</strong>
        <span>
          <IconButton icon="back" label={t('cal.prev_month')} onClick={() => setMonth(plusMonths(month, -1))} />
          <IconButton icon="chevronRight" label={t('cal.next_month')} onClick={() => setMonth(plusMonths(month, 1))} />
        </span>
      </div>
      <div className="cal-mini-grid" role="group" aria-label={dayText(month, { month: 'long', year: 'numeric' })}>
        {[1, 2, 3, 4, 5, 6, 7].map((w) => (
          <span key={w} className="cal-mini-wd" aria-hidden="true">
            {wdName(w, 'narrow')}
          </span>
        ))}
        {days.map((d) => (
          <button
            key={d}
            type="button"
            className={`cal-mini-d${d.slice(0, 7) !== month.slice(0, 7) ? ' out' : ''}${d === today ? ' today' : ''}${d >= range.from && d <= range.to ? ' in' : ''}`}
            onClick={() => onPick(d)}
            aria-label={longDay(d)}
            aria-current={d === today ? 'date' : undefined}
          >
            {Number(d.slice(8, 10))}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- one event -------------------------------------------------------------------------------------

function EventDialog({
  id,
  occ,
  home,
  onClose,
  onChanged,
  onEdit,
}: {
  id: string;
  occ: string | null;
  home: Home;
  onClose: () => void;
  onChanged: () => void;
  onEdit: (d: Detail, mode: 'edit' | 'duplicate') => void;
}) {
  const [d, setD] = useState<Detail | null>(null);
  const [scope, setScope] = useState<'one' | 'all'>('all');
  const [asking, setAsking] = useState<'delete' | null>(null);
  const { fail } = useUi();
  const { run, busy } = useSave();
  const load = useCallback(() => action<Detail>('calendar.get', { id, occ }).then(setD, (err) => (fail(err), onClose())), [id, occ, fail, onClose]);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, occ]);
  if (!d) return null;
  const tz = d.tz;
  const repeating = d.rule.repeat !== 'none';
  const answer = async (state: State) => {
    if (await run(() => action('calendar.respond', { id: d.id, occ: d.occ, state, scope: repeating ? scope : 'all' }), t(`cal.answered_${state}` as MessageKey))) {
      onChanged();
      void load();
    }
  };
  const counts = (['accepted', 'tentative', 'needs_action', 'declined'] as State[])
    .map((s) => [s, d.people.filter((p) => p.state === s).length + d.guests.filter((g) => g.state === s).length] as const)
    .filter(([, n]) => n);
  return (
    <>
      <Dialog
        title={d.name}
        onClose={onClose}
        size="md"
        footer={
          <div className="cal-dlg-foot">
            <span>
              <IconButton icon="download" label={t('cal.download_ics')} onClick={() => downloadIcs(d)} />
              {d.can.edit || d.state ? <IconButton icon="copy" label={t('cal.duplicate')} onClick={() => onEdit(d, 'duplicate')} /> : null}
            </span>
            <span className="cal-dlg-main">
              {d.can.edit ? (
                <>
                  <Button variant="ghost" icon="trash" onClick={() => setAsking('delete')}>
                    {t('cal.delete')}
                  </Button>
                  <Button variant="primary" icon="edit" onClick={() => onEdit(d, 'edit')}>
                    {t('cal.edit')}
                  </Button>
                </>
              ) : (
                <Button onClick={onClose}>{t('common.close')}</Button>
              )}
            </span>
          </div>
        }
      >
        <div className="cal-detail">
          <div className={`cal-detail-band tone-${d.tone}`} aria-hidden="true" />
          <p className="cal-detail-when">
            <Icon name="clock" size={18} />
            <span>
              <strong>{whenText(d, tz)}</strong>
              {repeating ? (
                <span className="muted small">
                  <Icon name="repeat" size={12} /> {ruleText(d.rule, d.rule.start_day)}
                </span>
              ) : d.series ? (
                <span className="muted small">
                  <Icon name="repeat" size={12} /> {t('cal.changed_occurrence')}
                </span>
              ) : null}
            </span>
          </p>
          {d.location ? (
            <p className="cal-detail-line">
              <Icon name="pin" size={18} />
              <a className="link" href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(d.location)}`} target="_blank" rel="noopener noreferrer">
                {d.location}
              </a>
            </p>
          ) : null}
          {d.video ? (
            <p className="cal-detail-line">
              <Icon name="video" size={18} />
              <a className="btn btn-secondary btn-sm" href={d.video} target="_blank" rel="noopener noreferrer">
                <span className="btn-label">{t('cal.join_call')}</span>
              </a>
              <span className="muted small cal-url">{d.video.replace(/^https:\/\//, '')}</span>
            </p>
          ) : null}
          {d.reminder !== null ? (
            <p className="cal-detail-line">
              <Icon name="bell" size={18} /> {t('cal.reminder_line', { when: minutesText(d.reminder) })}
            </p>
          ) : null}
          <p className="cal-detail-line">
            <Icon name="users" size={18} />
            <span>
              {t('cal.organised_by', { name: d.organizer.name })}
              {d.privacy === 'private' ? (
                <span className="muted small">
                  {' '}
                  · <Icon name="lock" size={12} /> {t('cal.private')}
                </span>
              ) : null}
              {d.show_as === 'free' ? <span className="muted small"> · {t('cal.shows_free')}</span> : null}
            </span>
          </p>
          {d.description ? <p className="cal-detail-desc">{d.description}</p> : null}
          {d.people.length > 1 || d.guests.length ? (
            <section className="cal-detail-people" aria-label={t('cal.people')}>
              <h3>
                {tn('cal.n_people', d.people.length + d.guests.length)}
                <span className="muted small"> · {counts.map(([s, n]) => `${n} ${t(`cal.st_${s}` as MessageKey).toLowerCase()}`).join(', ')}</span>
              </h3>
              <ul>
                {d.people.map((p) => (
                  <li key={p.id}>
                    <span className={`cal-avatar st-${p.state}`} aria-hidden="true">
                      {initials(p.name)}
                      <span className="cal-avatar-state">
                        <Icon name={STATE_ICON[p.state]} size={10} />
                      </span>
                    </span>
                    <span>
                      <span>
                        {p.name}
                        {p.id === home.me ? <span className="muted"> ({t('cal.you')})</span> : null}
                      </span>
                      <span className="muted small">{p.organizer ? t('cal.organiser') : t(`cal.st_${p.state}` as MessageKey)}</span>
                    </span>
                  </li>
                ))}
                {d.guests.map((g, i) => (
                  <li key={`g${i}`}>
                    <span className="cal-avatar guest" aria-hidden="true">
                      {initials(g.name)}
                    </span>
                    <span>
                      {g.name}
                      <span className="muted small">{g.email ? `${t('cal.guest')} · ${g.email}` : t('cal.guest')}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {d.can.respond ? (
            <section className="cal-rsvp" aria-label={t('cal.your_answer')}>
              <strong>{t('cal.going')}</strong>
              <div className="cal-rsvp-btns">
                {(['accepted', 'tentative', 'declined'] as const).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={d.state === s ? (s === 'declined' ? 'danger' : s === 'accepted' ? 'success' : 'primary') : 'secondary'}
                    icon={STATE_ICON[s]}
                    busy={busy}
                    onClick={() => void answer(s)}
                    aria-pressed={d.state === s}
                  >
                    {t(`cal.${s === 'accepted' ? 'yes' : s === 'tentative' ? 'maybe' : 'no'}` as MessageKey)}
                  </Button>
                ))}
              </div>
              {repeating ? (
                <label className="cal-rsvp-scope">
                  <span className="muted small">{t('cal.answer_for_label')}</span>
                  <select className="input cal-input-sm" value={scope} onChange={(e) => setScope(e.target.value as 'one' | 'all')}>
                    <option value="all">{t('cal.all_occurrences')}</option>
                    <option value="one">{t('cal.only_this')}</option>
                  </select>
                </label>
              ) : null}
            </section>
          ) : null}
        </div>
      </Dialog>
      {asking === 'delete' ? (
        repeating && d.occ ? (
          <ScopeDialog
            title={t('cal.delete_repeating')}
            confirm={t('cal.delete')}
            danger
            onClose={() => setAsking(null)}
            onPick={async (s) => {
              if (await run(() => action('calendar.remove', { id: d.id, occ: d.occ, scope: s }), t('cal.deleted'))) {
                onChanged();
                onClose();
              }
            }}
          />
        ) : (
          <Confirm
            title={t('cal.delete_title')}
            body={d.people.length > 1 ? t('cal.delete_body_people') : t('cal.delete_body')}
            confirm={t('cal.delete')}
            danger
            busy={busy}
            onClose={() => setAsking(null)}
            onConfirm={async () => {
              if (await run(() => action('calendar.remove', { id: d.id }), t('cal.deleted'))) {
                onChanged();
                onClose();
              }
            }}
          />
        )
      ) : null}
    </>
  );
}

function ScopeDialog({
  title,
  confirm,
  danger,
  onClose,
  onPick,
  allowFollowing = true,
}: {
  title: string;
  confirm: string;
  danger?: boolean;
  onClose: () => void;
  onPick: (s: Scope) => void;
  allowFollowing?: boolean;
}) {
  const [s, setS] = useState<Scope>('one');
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={() => onPick(s)}>
            {confirm}
          </Button>
        </>
      }
    >
      <fieldset className="cal-scope">
        <legend className="sr-only">{title}</legend>
        {(['one', ...(allowFollowing ? ['following'] : []), 'all'] as Scope[]).map((k) => (
          <label key={k} className={`cal-scope-opt${s === k ? ' on' : ''}`}>
            <input type="radio" name="scope" checked={s === k} onChange={() => setS(k)} />
            <span>
              <strong>{t(`cal.scope_${k}` as MessageKey)}</strong>
              <span className="muted small">{t(`cal.scope_${k}_hint` as MessageKey)}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </Dialog>
  );
}

/** An .ics file other calendar apps (Google, Outlook, Apple) can open. */
function downloadIcs(d: Detail) {
  const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
  const utc = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const local = (iso: string) => {
    const w = where(iso, d.tz);
    return `${w.day.replace(/-/g, '')}T${clockOf(w.min).replace(':', '')}00`;
  };
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ERP PRO//Calendar//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${d.id}${d.occ ? `-${d.occ}` : ''}@erp-pro`,
    `DTSTAMP:${utc(new Date().toISOString())}`,
  ];
  const repeating = d.rule.repeat !== 'none' && !d.occ;
  if (d.all_day) {
    lines.push(`DTSTART;VALUE=DATE:${where(d.starts, d.tz).day.replace(/-/g, '')}`, `DTEND;VALUE=DATE:${where(d.ends, d.tz).day.replace(/-/g, '')}`);
  } else if (repeating) {
    // Local times with the zone: a weekly 09:30 stays 09:30 after the clocks change.
    lines.push(`DTSTART;TZID=${d.tz}:${local(d.starts)}`, `DTEND;TZID=${d.tz}:${local(d.ends)}`);
  } else {
    lines.push(`DTSTART:${utc(d.starts)}`, `DTEND:${utc(d.ends)}`);
  }
  if (repeating && d.rule.rrule) lines.push(d.all_day ? d.rule.rrule : d.rule.rrule.replace(/UNTIL=(\d{8})/, 'UNTIL=$1T235959Z'));
  lines.push(`SUMMARY:${esc(d.name)}`);
  if (d.location) lines.push(`LOCATION:${esc(d.location)}`);
  const desc = [d.description, d.video ? `${t('cal.join_call')}: ${d.video}` : null].filter(Boolean).join('\n\n');
  if (desc) lines.push(`DESCRIPTION:${esc(desc)}`);
  if (d.video) lines.push(`URL:${d.video}`);
  for (const g of d.guests) if (g.email) lines.push(`ATTENDEE;CN=${esc(g.name)}:mailto:${g.email}`);
  if (d.reminder !== null) lines.push('BEGIN:VALARM', 'ACTION:DISPLAY', `DESCRIPTION:${esc(d.name)}`, `TRIGGER:-PT${d.reminder}M`, 'END:VALARM');
  lines.push('END:VEVENT', 'END:VCALENDAR');
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${d.name.replace(/[^\p{L}\p{N} _-]+/gu, '').trim() || 'event'}.ics`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// ---- making and changing an event ---------------------------------------------------------------------

type EditorStart = { day: string; from?: number; to?: number; allDay?: boolean } | { detail: Detail; duplicate?: boolean };

interface Form {
  name: string;
  allDay: boolean;
  day: string;
  from: number;
  endDay: string;
  to: number;
  location: string;
  video: string;
  description: string;
  privacy: 'public' | 'private';
  showAs: 'busy' | 'free';
  tone: ToneName;
  reminder: number | null;
  repeat: Repeat;
  interval: number;
  weekdays: number[];
  monthlyBy: 'date' | 'weekday';
  ends: 'never' | 'until' | 'count';
  until: string;
  count: number;
  attendees: string[];
  guests: { name: string; email: string }[];
}

function formFrom(start: EditorStart, home: Home): Form {
  const tz = home.tz;
  if ('detail' in start) {
    const d = start.detail;
    const a = where(d.starts, tz);
    const b = where(d.all_day ? Date.parse(d.ends) - 1 : d.ends, tz);
    return {
      name: start.duplicate ? t('cal.copy_of', { name: d.name }) : d.name,
      allDay: d.all_day,
      day: a.day,
      from: a.min,
      endDay: b.day,
      to: d.all_day ? a.min + 60 : b.min,
      location: d.location ?? '',
      video: d.video ?? '',
      description: d.description ?? '',
      privacy: d.privacy,
      showAs: d.show_as,
      tone: d.tone,
      reminder: d.reminder,
      repeat: d.rule.repeat,
      interval: d.rule.interval,
      weekdays: d.rule.weekdays ?? [isoWeekday(d.rule.start_day)],
      monthlyBy: d.rule.monthly_by,
      ends: d.rule.count ? 'count' : d.rule.until ? 'until' : 'never',
      until: d.rule.until ?? plusMonths(a.day, 3),
      count: d.rule.count ?? 10,
      attendees: d.people.filter((p) => p.id !== home.me && !(start.duplicate && p.organizer)).map((p) => p.id),
      guests: d.guests.map((g) => ({ name: g.name, email: g.email })),
    };
  }
  const now = where(home.now, tz);
  const from = start.from ?? (start.day === now.day ? Math.min(23 * 60, Math.ceil((now.min + 1) / 30) * 30) : 9 * 60);
  const to = start.to ?? Math.min(DAY_MIN - 15, from + 60);
  return {
    name: '',
    allDay: !!start.allDay,
    day: start.day,
    from,
    endDay: to >= DAY_MIN ? plusDays(start.day, 1) : start.day,
    to: to >= DAY_MIN ? 0 : to,
    location: '',
    video: '',
    description: '',
    privacy: 'public',
    showAs: 'busy',
    tone: 'blue',
    reminder: 15,
    repeat: 'none',
    interval: 1,
    weekdays: [isoWeekday(start.day)],
    monthlyBy: 'date',
    ends: 'never',
    until: plusMonths(start.day, 3),
    count: 10,
    attendees: [],
    guests: [],
  };
}

const TIMES = Array.from({ length: 96 }, (_, i) => i * 15);

function Editor({ start, home, onClose, onSaved }: { start: EditorStart; home: Home; onClose: () => void; onSaved: (id: string | null) => void }) {
  const detail = 'detail' in start && !start.duplicate ? start.detail : null;
  const [f, setF] = useState<Form>(() => formFrom(start, home));
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [more, setMore] = useState(() => !!(detail && (detail.description || detail.privacy === 'private' || detail.show_as === 'free')));
  const [guest, setGuest] = useState({ name: '', email: '' });
  const { run, busy } = useSave();
  const tz = home.tz;
  const up = (patch: Partial<Form>) => setF((x) => ({ ...x, ...patch }));
  const length = dayDiff(f.day, f.endDay) * DAY_MIN + f.to - f.from;
  const setStart = (day: string, from: number) => {
    // The end keeps the same distance from the start.
    const endAt = dayDiff(f.day, day) * DAY_MIN + from - f.from + (dayDiff(f.day, f.endDay) * DAY_MIN + f.to);
    up({
      day,
      from,
      endDay: plusDays(f.day, Math.floor(endAt / DAY_MIN)),
      to: ((endAt % DAY_MIN) + DAY_MIN) % DAY_MIN,
      weekdays: f.repeat === 'weekly' && f.weekdays.length === 1 ? [isoWeekday(day)] : f.weekdays,
    });
  };
  const repeating = detail ? detail.rule.repeat !== 'none' : false;
  const payload = (scope: Scope) => {
    const starts = localTime(f.day, f.from, tz).toISOString();
    const ends = localTime(f.endDay, f.to, tz).toISOString();
    return {
      id: detail?.id ?? null,
      occ: detail?.occ ?? null,
      scope,
      name: f.name,
      all_day: f.allDay,
      starts: f.allDay ? null : starts,
      ends: f.allDay ? null : ends,
      day: f.allDay ? f.day : null,
      end_day: f.allDay ? f.endDay : null,
      location: f.location,
      video: f.video,
      description: f.description,
      privacy: f.privacy,
      show_as: f.showAs,
      tone: f.tone,
      reminder: f.reminder,
      repeat: scope === 'one' || detail?.series ? 'none' : f.repeat,
      interval: f.interval,
      weekdays: f.repeat === 'weekly' ? f.weekdays : null,
      monthly_by: f.monthlyBy,
      until: f.ends === 'until' ? f.until : null,
      count: f.ends === 'count' ? f.count : null,
      attendees: f.attendees,
      guests: f.guests,
    };
  };
  const check = () => {
    if (!f.name.trim()) return t('cal.err_title');
    if (!f.allDay && length <= 0) return t('cal.err_end');
    if (f.allDay && f.endDay < f.day) return t('cal.err_end');
    if (f.video && !/^https:\/\/\S+$/i.test(f.video.trim())) return t('cal.err_video');
    if (f.repeat !== 'none' && (f.allDay ? dayDiff(f.day, f.endDay) > 0 : length > DAY_MIN)) return t('cal.err_repeat_long');
    return null;
  };
  const save = async (scope: Scope) => {
    let id: string | null = null;
    if (await run(async () => (id = (await action<{ id: string }>('calendar.save', payload(scope))).id), detail ? t('cal.saved') : t('cal.created'))) onSaved(id);
  };
  const submit = () => {
    const err = check();
    setError(err);
    if (err) return;
    if (detail && repeating && detail.occ) setAsking(true);
    else void save('all');
  };
  const timeOptions = (min = -1) =>
    TIMES.filter((m) => m > min).map((m) => (
      <option key={m} value={m}>
        {timeText(localTime(f.day, m, tz).toISOString(), tz)}
        {min >= 0 ? ` (${durationText(m - min)})` : ''}
      </option>
    ));
  const sameDay = f.endDay === f.day;
  const repeatChoices: { key: string; label: string; patch: Partial<Form> }[] = [
    { key: 'none', label: t('cal.no_repeat'), patch: { repeat: 'none' } },
    { key: 'daily', label: t('cal.every_day'), patch: { repeat: 'daily', interval: 1 } },
    { key: 'weekdays', label: t('cal.every_workday'), patch: { repeat: 'weekly', interval: 1, weekdays: [1, 2, 3, 4, 5] } },
    { key: 'weekly', label: t('cal.every_week_on', { days: wdName(isoWeekday(f.day), 'long') }), patch: { repeat: 'weekly', interval: 1, weekdays: [isoWeekday(f.day)] } },
    { key: 'monthly', label: t('cal.every_month', { on: t('cal.on_day', { n: Number(f.day.slice(8, 10)) }) }), patch: { repeat: 'monthly', interval: 1, monthlyBy: 'date' } },
    {
      key: 'monthly_wd',
      label: t('cal.every_month', { on: t('cal.on_the', { nth: ordinal(nthOfMonth(f.day)), day: wdName(isoWeekday(f.day), 'long') }) }),
      patch: { repeat: 'monthly', interval: 1, monthlyBy: 'weekday' },
    },
    { key: 'yearly', label: t('cal.every_year', { on: dayText(f.day, { day: 'numeric', month: 'long' }) }), patch: { repeat: 'yearly', interval: 1 } },
    { key: 'custom', label: t('cal.custom_repeat'), patch: {} },
  ];
  const preset =
    f.repeat === 'none'
      ? 'none'
      : f.interval !== 1
        ? 'custom'
        : f.repeat === 'daily'
          ? 'daily'
          : f.repeat === 'weekly'
            ? f.weekdays.join() === '1,2,3,4,5'
              ? 'weekdays'
              : f.weekdays.length === 1 && f.weekdays[0] === isoWeekday(f.day)
                ? 'weekly'
                : 'custom'
            : f.repeat === 'monthly'
              ? f.monthlyBy === 'date'
                ? 'monthly'
                : 'monthly_wd'
              : 'yearly';
  const [custom, setCustom] = useState(preset === 'custom');
  const repeatLocked = !!detail?.series;
  const inPeople = [home.me, ...f.attendees];
  return (
    <>
      <Dialog
        title={detail ? t('cal.edit_event') : t('cal.new_event')}
        onClose={onClose}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" busy={busy} onClick={submit}>
              {detail ? t('cal.save') : f.attendees.length || f.guests.length ? t('cal.save_invite') : t('cal.save')}
            </Button>
          </>
        }
      >
        <form
          className="cal-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {error ? (
            <p className="form-error" role="alert">
              <Icon name="alert" size={16} /> {error}
            </p>
          ) : null}
          <input
            className="input cal-form-title"
            value={f.name}
            onChange={(e) => up({ name: e.target.value })}
            placeholder={t('cal.title_placeholder')}
            aria-label={t('cal.event_title')}
            maxLength={200}
            data-autofocus
            autoFocus
          />
          <div className="cal-form-when">
            <div className="cal-form-row">
              <Icon name="clock" size={18} />
              <input className="input" type="date" value={f.day} onChange={(e) => e.target.value && setStart(e.target.value, f.from)} aria-label={t('cal.start_day')} />
              {f.allDay ? null : (
                <select className="input" value={f.from} onChange={(e) => setStart(f.day, Number(e.target.value))} aria-label={t('cal.start_time')}>
                  {timeOptions()}
                </select>
              )}
              <span className="muted">–</span>
              {f.allDay ? null : (
                <select
                  className="input"
                  value={sameDay ? f.to : -1}
                  onChange={(e) => {
                    const m = Number(e.target.value);
                    if (m >= 0) up({ to: m, endDay: f.day });
                  }}
                  aria-label={t('cal.end_time')}
                >
                  {sameDay ? null : <option value={-1}>{timeText(localTime(f.endDay, f.to, tz).toISOString(), tz)}</option>}
                  {timeOptions(f.from)}
                </select>
              )}
              {f.allDay || !sameDay ? (
                <input className="input" type="date" value={f.endDay} min={f.day} onChange={(e) => e.target.value && up({ endDay: e.target.value })} aria-label={t('cal.end_day')} />
              ) : null}
            </div>
            <div className="cal-form-row wrap">
              <label className="cal-inline">
                <Switch checked={f.allDay} onChange={(v) => up({ allDay: v, endDay: v ? f.day : f.endDay })} label={t('cal.all_day')} />
                {t('cal.all_day')}
              </label>
              {!f.allDay && sameDay ? (
                <button type="button" className="link-button small" onClick={() => up({ endDay: plusDays(f.day, 1) })}>
                  {t('cal.ends_other_day')}
                </button>
              ) : null}
              <select
                className="input cal-input-sm cal-repeat"
                value={custom ? 'custom' : preset}
                disabled={repeatLocked}
                onChange={(e) => {
                  const c = repeatChoices.find((x) => x.key === e.target.value)!;
                  setCustom(c.key === 'custom');
                  up(c.key === 'custom' && f.repeat === 'none' ? { repeat: 'weekly', weekdays: [isoWeekday(f.day)] } : c.patch);
                }}
                aria-label={t('cal.repeat')}
                title={repeatLocked ? t('cal.repeat_locked') : undefined}
              >
                {repeatChoices.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            {custom && f.repeat !== 'none' ? (
              <div className="cal-custom">
                <div className="cal-form-row wrap">
                  <span>{t('cal.every')}</span>
                  <input
                    className="input cal-input-sm cal-num"
                    type="number"
                    min={1}
                    max={99}
                    value={f.interval}
                    onChange={(e) => up({ interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })}
                    aria-label={t('cal.interval')}
                  />
                  <select className="input cal-input-sm" value={f.repeat} onChange={(e) => up({ repeat: e.target.value as Repeat })} aria-label={t('cal.unit')}>
                    {(['daily', 'weekly', 'monthly', 'yearly'] as const).map((r) => (
                      <option key={r} value={r}>
                        {tn(`cal.unit_${r}` as 'cal.unit_daily', f.interval)}
                      </option>
                    ))}
                  </select>
                </div>
                {f.repeat === 'weekly' ? (
                  <div className="cal-wds" role="group" aria-label={t('cal.on_days')}>
                    {[1, 2, 3, 4, 5, 6, 7].map((w) => {
                      const on = f.weekdays.includes(w);
                      return (
                        <button
                          key={w}
                          type="button"
                          className={`chip${on ? ' active' : ''}`}
                          aria-pressed={on}
                          aria-label={wdName(w, 'long')}
                          onClick={() => up({ weekdays: on ? (f.weekdays.length > 1 ? f.weekdays.filter((x) => x !== w) : f.weekdays) : [...f.weekdays, w].sort() })}
                        >
                          {wdName(w, 'narrow')}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {f.repeat === 'monthly' ? (
                  <select className="input cal-input-sm" value={f.monthlyBy} onChange={(e) => up({ monthlyBy: e.target.value as 'date' | 'weekday' })} aria-label={t('cal.monthly_on')}>
                    <option value="date">{t('cal.on_day', { n: Number(f.day.slice(8, 10)) })}</option>
                    <option value="weekday">{t('cal.on_the', { nth: ordinal(nthOfMonth(f.day)), day: wdName(isoWeekday(f.day), 'long') })}</option>
                  </select>
                ) : null}
              </div>
            ) : null}
            {f.repeat !== 'none' && !repeatLocked ? (
              <div className="cal-form-row wrap cal-ends">
                <span className="muted small">{t('cal.ends')}</span>
                {(['never', 'until', 'count'] as const).map((k) => (
                  <label key={k} className="cal-inline small">
                    <input type="radio" name="ends" checked={f.ends === k} onChange={() => up({ ends: k })} />
                    {t(`cal.ends_${k}` as MessageKey)}
                  </label>
                ))}
                {f.ends === 'until' ? (
                  <input className="input cal-input-sm" type="date" min={f.day} value={f.until} onChange={(e) => e.target.value && up({ until: e.target.value })} aria-label={t('cal.ends_until')} />
                ) : null}
                {f.ends === 'count' ? (
                  <input
                    className="input cal-input-sm cal-num"
                    type="number"
                    min={1}
                    max={730}
                    value={f.count}
                    onChange={(e) => up({ count: Math.max(1, Math.min(730, Number(e.target.value) || 1)) })}
                    aria-label={t('cal.ends_count')}
                  />
                ) : null}
              </div>
            ) : null}
            {f.repeat !== 'none' ? (
              <p className="muted small cal-rule-text">
                <Icon name="repeat" size={12} />{' '}
                {ruleText(
                  { repeat: f.repeat, interval: f.interval, weekdays: f.weekdays, monthly_by: f.monthlyBy, until: f.ends === 'until' ? f.until : null, count: f.ends === 'count' ? f.count : null },
                  f.day,
                )}
              </p>
            ) : null}
          </div>

          <section className="cal-form-sec" aria-labelledby="cal-f-people">
            <h3 id="cal-f-people">
              <Icon name="users" size={18} /> {t('cal.people')}
            </h3>
            <PeopleField home={home} value={f.attendees} onChange={(attendees) => up({ attendees })} />
            {!f.allDay && f.attendees.length ? (
              <FindTime home={home} people={inPeople} day={f.day} from={f.from} length={length} except={detail?.id ?? null} onPick={(day, from) => setStart(day, from)} />
            ) : null}
            <div className="cal-guests">
              {f.guests.map((g, i) => (
                <span key={i} className="chip active">
                  <Icon name="mail" size={12} /> {g.name}
                  {g.email ? <span className="muted"> · {g.email}</span> : null}
                  <button type="button" className="chip-x" aria-label={t('cal.remove_guest', { name: g.name })} onClick={() => up({ guests: f.guests.filter((_, j) => j !== i) })}>
                    <Icon name="x" size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="cal-form-row wrap">
              <input
                className="input cal-input-sm"
                value={guest.name}
                onChange={(e) => setGuest({ ...guest, name: e.target.value })}
                placeholder={t('cal.guest_name')}
                aria-label={t('cal.guest_name')}
                maxLength={120}
              />
              <input
                className="input cal-input-sm"
                type="email"
                value={guest.email}
                onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                placeholder={t('cal.guest_email')}
                aria-label={t('cal.guest_email')}
                maxLength={200}
              />
              <Button
                size="sm"
                icon="plus"
                disabled={!guest.name.trim()}
                onClick={() => {
                  up({ guests: [...f.guests, { name: guest.name.trim(), email: guest.email.trim() }] });
                  setGuest({ name: '', email: '' });
                }}
              >
                {t('cal.add_guest')}
              </Button>
            </div>
            {f.guests.length ? <p className="muted small">{t('cal.guests_hint')}</p> : null}
          </section>

          <div className="cal-form-grid">
            <Field label={t('cal.location')}>
              <input className="input" value={f.location} onChange={(e) => up({ location: e.target.value })} placeholder={t('cal.location_placeholder')} maxLength={300} />
            </Field>
            <Field label={t('cal.video')} hint={t('cal.video_hint')}>
              <input className="input" type="url" value={f.video} onChange={(e) => up({ video: e.target.value })} placeholder="https://" maxLength={500} />
            </Field>
            <Field label={t('cal.reminder')}>
              <select className="input" value={f.reminder ?? ''} onChange={(e) => up({ reminder: e.target.value === '' ? null : Number(e.target.value) })}>
                <option value="">{t('cal.no_reminder')}</option>
                {home.reminders.map((m) => (
                  <option key={m} value={m}>
                    {minutesText(m)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="field">
              <span className="field-label">{t('cal.colour')}</span>
              <div className="cal-tones" role="radiogroup" aria-label={t('cal.colour')}>
                {TONES.map((x) => (
                  <button
                    key={x}
                    type="button"
                    role="radio"
                    aria-checked={f.tone === x}
                    aria-label={t(`cal.tone_${x}` as MessageKey)}
                    className={`cal-tone tone-${x}${f.tone === x ? ' on' : ''}`}
                    onClick={() => up({ tone: x })}
                  >
                    {f.tone === x ? <Icon name="check" size={14} /> : null}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {more ? (
            <div className="cal-form-grid">
              <Field label={t('cal.description')}>
                <textarea className="input" rows={4} value={f.description} onChange={(e) => up({ description: e.target.value })} placeholder={t('cal.description_placeholder')} maxLength={5000} />
              </Field>
              <div className="cal-form-opts">
                <label className="cal-opt">
                  <Switch checked={f.privacy === 'private'} onChange={(v) => up({ privacy: v ? 'private' : 'public' })} label={t('cal.private')} />
                  <span>
                    <strong>{t('cal.private')}</strong>
                    <span className="muted small">{t('cal.private_hint')}</span>
                  </span>
                </label>
                <label className="cal-opt">
                  <Switch checked={f.showAs === 'free'} onChange={(v) => up({ showAs: v ? 'free' : 'busy' })} label={t('cal.shows_free')} />
                  <span>
                    <strong>{t('cal.shows_free')}</strong>
                    <span className="muted small">{t('cal.shows_free_hint')}</span>
                  </span>
                </label>
              </div>
            </div>
          ) : (
            <button type="button" className="link-button small" onClick={() => setMore(true)}>
              <Icon name="plus" size={14} /> {t('cal.more_options')}
            </button>
          )}
        </form>
      </Dialog>
      {asking ? (
        <ScopeDialog
          title={t('cal.save_repeating')}
          confirm={t('cal.save')}
          onClose={() => setAsking(false)}
          onPick={(s) => {
            setAsking(false);
            void save(s);
          }}
        />
      ) : null}
    </>
  );
}

const durationText = (m: number) => (m < 60 ? t('cal.dur_min', { n: m }) : m % 60 ? t('cal.dur_h_min', { h: Math.floor(m / 60), m: m % 60 }) : tn('cal.dur_h', m / 60));

function PeopleField({ home, value, onChange }: { home: Home; value: string[]; onChange: (ids: string[]) => void }) {
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(false);
  const found = home.people.filter((p) => p.id !== home.me && !value.includes(p.id) && q.trim() && `${p.name} ${p.username}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6);
  return (
    <div className="cal-people">
      <div className="cal-chosen">
        {value.map((id) => {
          const p = home.people.find((x) => x.id === id);
          return p ? (
            <span key={id} className="chip active">
              {p.name}
              <button type="button" className="chip-x" aria-label={t('cal.uninvite', { name: p.name })} onClick={() => onChange(value.filter((x) => x !== id))}>
                <Icon name="x" size={12} />
              </button>
            </span>
          ) : null;
        })}
      </div>
      <div className="cal-people-search">
        <div className="ds-search">
          <Icon name="search" size={16} />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setFocus(true)}
            onBlur={() => setTimeout(() => setFocus(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && found[0]) {
                e.preventDefault();
                onChange([...value, found[0].id]);
                setQ('');
              }
            }}
            placeholder={t('cal.invite_people')}
            aria-label={t('cal.invite_people')}
            role="combobox"
            aria-expanded={focus && found.length > 0}
            aria-controls="cal-people-list"
          />
        </div>
        {focus && found.length ? (
          <ul className="cal-suggest" id="cal-people-list" role="listbox">
            {found.map((p) => (
              <li key={p.id} role="option" aria-selected="false">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange([...value, p.id]);
                    setQ('');
                  }}
                >
                  <span className="cal-avatar" aria-hidden="true">
                    {initials(p.name)}
                  </span>
                  <span>
                    {p.name} <span className="muted small">@{p.username}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

interface FreeBusy {
  day: string;
  people: { id: string; name: string; away: boolean; busy: { from: number; to: number }[] }[];
}

/** Everyone's busy time on the day, the chosen time over it, and the next time that suits all. */
function FindTime({
  home,
  people,
  day,
  from,
  length,
  except,
  onPick,
}: {
  home: Home;
  people: string[];
  day: string;
  from: number;
  length: number;
  except: string | null;
  onPick: (day: string, from: number) => void;
}) {
  const [fb, setFb] = useState<FreeBusy | null>(null);
  const [searching, setSearching] = useState(false);
  const { fail, toast } = useUi();
  const key = JSON.stringify([people, day, except]);
  useEffect(() => {
    let alive = true;
    const [users, d, ex] = JSON.parse(key) as [string[], string, string | null];
    action<FreeBusy>('calendar.freebusy', { users, day: d, except: ex }).then(
      (r) => alive && setFb(r),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [key]);
  const START = 7 * 60;
  const END = 21 * 60;
  const pct = (m: number) => `${((Math.max(START, Math.min(END, m)) - START) / (END - START)) * 100}%`;
  const clashes = (r: FreeBusy, at: number) => r.people.filter((p) => p.away || p.busy.some((b) => b.from < at + length && b.to > at));
  const conflicts = fb ? clashes(fb, from) : [];
  const nextFree = async () => {
    setSearching(true);
    try {
      for (let i = 0; i < 14; i++) {
        const d = plusDays(day, i);
        if (isoWeekday(d) > 6) continue;
        const r = i === 0 && fb ? fb : await action<FreeBusy>('calendar.freebusy', { users: people, day: d, except });
        const first = i === 0 ? Math.max(START, Math.ceil((from + SNAP) / SNAP) * SNAP) : 8 * 60;
        for (let at = first; at + length <= 19 * 60; at += SNAP) {
          if (d === home.today && at < where(Date.now(), home.tz).min) continue;
          if (!clashes(r, at).length) {
            onPick(d, at);
            return;
          }
        }
      }
      toast(t('cal.no_free_time'), 'info');
    } catch (err) {
      fail(err);
    } finally {
      setSearching(false);
    }
  };
  const hours = Array.from({ length: (END - START) / 60 + 1 }, (_, i) => START + i * 60);
  return (
    <div className="cal-find">
      <div className="cal-find-head">
        <strong>
          {t('cal.find_time')} · {dayText(day, { weekday: 'short', day: 'numeric', month: 'short' })}
        </strong>
        {fb ? (
          conflicts.length ? (
            <StatusBadge tone="warn" icon="alert" label={tn('cal.n_busy', conflicts.length)} />
          ) : (
            <StatusBadge tone="ok" icon="checkCircle" label={t('cal.everyone_free')} />
          )
        ) : null}
        <Button size="sm" icon="search" busy={searching} onClick={() => void nextFree()}>
          {t('cal.next_free')}
        </Button>
      </div>
      <div className="cal-find-grid">
        <div className="cal-find-scale" aria-hidden="true">
          {hours.map((h) => (
            <span key={h} style={{ left: pct(h) }}>
              {clockOf(h).slice(0, 2)}
            </span>
          ))}
        </div>
        {(fb?.people ?? people.map((id) => ({ id, name: home.people.find((p) => p.id === id)?.name ?? '', away: false, busy: [] }))).map((p) => (
          <div key={p.id} className="cal-find-row">
            <span className="cal-find-name">{p.id === home.me ? t('cal.you') : p.name}</span>
            <div
              className={`cal-find-track${p.away ? ' away' : ''}`}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                const m = START + Math.round((((e.clientX - r.left) / r.width) * (END - START)) / SNAP) * SNAP;
                onPick(day, Math.max(0, Math.min(DAY_MIN - length, m - Math.round(length / 2 / SNAP) * SNAP)));
              }}
              role="presentation"
              title={t('cal.click_to_move')}
            >
              {p.away ? <span className="cal-find-away">{t('cal.away')}</span> : null}
              {p.busy.map((b, i) => (
                <span key={i} className="cal-find-busy" style={{ left: pct(b.from), width: `calc(${pct(b.to)} - ${pct(b.from)})` }} />
              ))}
              <span className={`cal-find-pick${conflicts.some((c) => c.id === p.id) ? ' clash' : ''}`} style={{ left: pct(from), width: `calc(${pct(from + length)} - ${pct(from)})` }} />
            </div>
          </div>
        ))}
      </div>
      <p className="muted small">{t('cal.find_time_hint')}</p>
    </div>
  );
}
